import { Button } from '@/components/Button';
import LoadingSpinner from 'components/LoadingSpinner';
import { useState } from 'react';

interface OrganizationNameStepProps {
    onSubmit: (organizationName: string) => void;
    onSkip: () => void;
    isSaving: boolean;
}

/**
 * Second onboarding step, naming the organization.
 *
 * A tenant created by a sign-up is named after the email address the user signed up with, so
 * without this step that address is what the app shows as the organization name.
 */
export const OrganizationNameStep: React.FC<OrganizationNameStepProps> = ({ onSubmit, onSkip, isSaving }) => {
    const [organizationName, setOrganizationName] = useState('');

    const trimmedName = organizationName.trim();

    return (
        <form
            data-testid='onboardingOrganizationNameStep'
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit(trimmedName);
            }}
        >
            <h1 className='mt-16 text-5xl font-medium'>Name your organization</h1>

            <input
                type='text'
                name='organisationName'
                aria-label='Organization name'
                autoFocus
                maxLength={128}
                placeholder='e.g. Northwind Technologies'
                className={
                    'w-full !text-lg bg-transparent border-0 border-b-2 border-dividerSecondary focus:border-dividerPrimary focus:ring-0 py-input leading-input text-textPrimary placeholder-textIncomplete mt-10'
                }
                value={organizationName}
                onChange={(event) => setOrganizationName(event.target.value)}
                data-testid='onboardingOrganizationNameInput'
            />

            <div className='flex items-center justify-end gap-6 mt-6'>
                <Button
                    variant='tertiary'
                    type='button'
                    onClick={onSkip}
                    disabled={isSaving}
                    data-testid='onboardingOrganizationNameSkip'
                >
                    Skip
                </Button>

                <Button
                    variant='primary'
                    type='submit'
                    disabled={trimmedName.length === 0 || isSaving}
                    icon={isSaving ? <LoadingSpinner size={18} /> : undefined}
                    data-testid='onboardingOrganizationNameDone'
                >
                    Done
                </Button>
            </div>
        </form>
    );
};
